import { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Input, Label, Spinner, Button,
} from "reactstrap";
import Select from "react-select";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import {
  createInvoice,
  updateInvoice,
  uploadFileToInvoice,
  removeInvoiceAttachment,
  getInvoiceSignedUrls,
  INVOICE_STATUSES,
  formatCurrency,
} from "./Api";
import { Previewer } from "../../../../components/DocumentPreviewer/Previewer";
import { getFileExtension, imageTypes, pdfTypes, officeTypes, videoTypes }
  from "../../../../components/DocumentPreviewer/utils";

const selectStyles = {
  control: (base, { isFocused }) => ({
    ...base,
    borderColor: isFocused ? "#6366F1" : "#E2E8F0",
    borderRadius: 8,
    minHeight: 38,
    fontSize: "0.83rem",
    boxShadow: isFocused ? "0 0 0 2px rgba(99,102,241,0.15)" : "none",
    "&:hover": { borderColor: "#6366F1" },
  }),
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    fontSize: "0.83rem",
    background: isSelected ? "#4F46E5" : isFocused ? "#EEF2FF" : "white",
    color: isSelected ? "white" : "#0F172A",
  }),
  menu: (base) => ({ ...base, borderRadius: 8, border: "1px solid #E2E8F0", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const LABEL_STYLE = {
  fontSize: "0.72rem", fontWeight: 700, color: "#334155",
  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6,
};

const INPUT_STYLE = {
  borderColor: "#E2E8F0",
  borderRadius: 8,
  fontSize: "0.85rem",
  height: 38,
};

const CURRENCY_OPTIONS = [
  { value: "CAD", label: "CAD" },
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
  { value: "GBP", label: "GBP" },
  { value: "INR", label: "INR" },
];

const getFileTypeFromKey = (keyOrName) => {
  const ext = getFileExtension(keyOrName).toLowerCase();
  if (pdfTypes.includes(ext)) return "pdf";
  if (imageTypes.includes(ext)) return "image";
  if (videoTypes.includes(ext)) return "video";
  if (officeTypes.includes(ext)) return "office";
  return "unknown";
};

const FilePreviewModal = ({ isOpen, toggle, signedUrl, fileKey }) => {
  const type = fileKey ? getFileTypeFromKey(fileKey) : "unknown";
  const fileName = fileKey ? fileKey.split("/").pop() : "Attachment";
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" style={{ zIndex: 12000 }}>
      <ModalHeader toggle={toggle} style={{ background: "#0F172A", borderBottom: "none", padding: "14px 20px" }}>
        <span style={{ color: "white", fontWeight: 700, fontSize: "0.9rem" }}>{fileName}</span>
      </ModalHeader>
      <ModalBody style={{ padding: 0, background: "#0F172A" }}>
        {signedUrl ? (
          <Previewer signedUrl={signedUrl} type={type} height="72vh" width="100%" />
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "72vh" }}>
            <Spinner style={{ color: "#A5B4FC" }} />
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ background: "#0F172A", border: "none", padding: "12px 20px" }}>
        <Button color="secondary" onClick={toggle}>Close</Button>
        {signedUrl && (
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={fileName}
            className="iv-btn iv-btn--primary"
            style={{ textDecoration: "none" }}
          >
            <FeatherIcon icon="download" size={14} style={{ marginRight: 4 }} />
            Download
          </a>
        )}
      </ModalFooter>
    </Modal>
  );
};

const InvoiceFormModal = ({
  isOpen,
  onClose,
  invoiceData,
  isNew,
  properties,
  categoryOptions,
  prefilledPropertyId,
  prefilledBuildingName,
  prefilledPropertyLabel,
}) => {
  const initial = useMemo(() => ({
    title: "",
    vendorName: "",
    vendorEmail: "",
    vendorPhone: "",
    amount: "",
    currency: "CAD",
    invoiceDate: moment().format("YYYY-MM-DD"),
    dueDate: "",
    propertyId: prefilledPropertyId || "",
    buildingName: prefilledBuildingName || "",
    categoryId: "",
    status: "Unpaid",
    description: "",
    referenceNo: "",
  }), [prefilledPropertyId, prefilledBuildingName]);

  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [signedUrls, setSignedUrls] = useState({});
  const [uploading, setUploading] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [previewPendingId, setPreviewPendingId] = useState(null);
  const fileInputRef = useRef(null);

  const revokePendingPreviews = (files) => {
    files.forEach((f) => {
      if (f.previewUrl) {
        try { URL.revokeObjectURL(f.previewUrl); } catch { /* ignore */ }
      }
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    if (isNew) {
      setForm(initial);
      setCreatedId(null);
      setAttachments([]);
      setSignedUrls({});
      revokePendingPreviews(pendingFiles);
      setPendingFiles([]);
    } else if (invoiceData) {
      setForm({
        title: invoiceData.title || "",
        vendorName: invoiceData.vendorName || "",
        vendorEmail: invoiceData.vendorEmail || "",
        vendorPhone: invoiceData.vendorPhone || "",
        amount: invoiceData.amount ?? "",
        currency: invoiceData.currency || "CAD",
        invoiceDate: invoiceData.invoiceDate ? moment(invoiceData.invoiceDate).format("YYYY-MM-DD") : "",
        dueDate: invoiceData.dueDate ? moment(invoiceData.dueDate).format("YYYY-MM-DD") : "",
        propertyId: invoiceData.property?.propertyId || prefilledPropertyId || "",
        buildingName: invoiceData.property?.buildingName || prefilledBuildingName || "",
        categoryId: invoiceData.categoryId || invoiceData.category?._id || "",
        status: invoiceData.status || "Unpaid",
        description: invoiceData.description || "",
        referenceNo: invoiceData.referenceNo || "",
      });
      setCreatedId(invoiceData._id);
      setAttachments(invoiceData.attachments || []);
    }
    setErrors({});
  }, [isOpen, isNew, invoiceData, initial, prefilledPropertyId, prefilledBuildingName]);

  useEffect(() => {
    if (!attachments.length) {
      setSignedUrls({});
      return;
    }
    const missing = attachments.filter((k) => !signedUrls[k]);
    if (!missing.length) return;
    (async () => {
      const urls = await getInvoiceSignedUrls(missing);
      setSignedUrls((prev) => ({ ...prev, ...urls }));
    })();
  }, [attachments]); // eslint-disable-line

  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.title?.trim()) e.title = "Title is required";
    if (!form.vendorName?.trim()) e.vendorName = "Vendor name is required";
    if (form.amount === "" || Number.isNaN(Number(form.amount)) || Number(form.amount) < 0) {
      e.amount = "A valid amount is required";
    }
    if (!form.propertyId) e.propertyId = "Property is required";
    if (!form.buildingName?.trim()) e.buildingName = "Building is required";
    if (!form.categoryId) e.categoryId = "Category is required";
    if (!form.invoiceDate) e.invoiceDate = "Invoice date is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const uploadPendingFiles = async (id, files) => {
    const succeeded = [];
    const failed = [];
    for (const pf of files) {
      try {
        const key = await uploadFileToInvoice(id, pf.file);
        succeeded.push(key);
      } catch (err) {
        console.error("upload failed", err);
        failed.push(pf.file.name);
      }
    }
    return { succeeded, failed };
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        vendorName: form.vendorName.trim(),
        vendorEmail: form.vendorEmail.trim(),
        vendorPhone: form.vendorPhone.trim(),
        amount: Number(form.amount),
        currency: form.currency,
        invoiceDate: form.invoiceDate,
        dueDate: form.dueDate || undefined,
        propertyId: form.propertyId,
        buildingName: form.buildingName.trim(),
        categoryId: form.categoryId,
        status: form.status,
        description: form.description.trim(),
        referenceNo: form.referenceNo.trim(),
      };
      if (isNew) {
        const res = await createInvoice(payload);
        const id = res?.data?._id;
        if (!id) {
          toast.error("Invoice was not created.");
          return;
        }
        setCreatedId(id);

        if (pendingFiles.length > 0) {
          setUploading(true);
          const { succeeded, failed } = await uploadPendingFiles(id, pendingFiles);
          setAttachments((prev) => [...prev, ...succeeded]);
          revokePendingPreviews(pendingFiles);
          setPendingFiles([]);
          setUploading(false);
          if (failed.length === 0) {
            toast.success(`Invoice created with ${succeeded.length} attachment${succeeded.length !== 1 ? "s" : ""}.`);
          } else {
            toast.success(`Invoice created. ${succeeded.length} attachment${succeeded.length !== 1 ? "s" : ""} uploaded, ${failed.length} failed.`);
          }
        } else {
          toast.success("Invoice created.");
        }
      } else {
        await updateInvoice(invoiceData._id, payload);
        toast.success("Invoice updated.");
      }
    } catch {
      // toast handled in api helper
    } finally {
      setSaving(false);
    }
  };

  const invoiceId = createdId || invoiceData?._id;

  const handleFiles = async (files) => {
    if (!files?.length) return;

    if (!invoiceId) {
      // Stage files locally — they'll upload right after the invoice is created
      const staged = Array.from(files).map((file) => {
        let previewUrl = "";
        try {
          if (file.type?.startsWith("image/")) {
            previewUrl = URL.createObjectURL(file);
          }
        } catch { /* ignore */ }
        return {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          previewUrl,
        };
      });
      setPendingFiles((prev) => [...prev, ...staged]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        try {
          const key = await uploadFileToInvoice(invoiceId, file);
          setAttachments((prev) => [...prev, key]);
        } catch (err) {
          console.error("upload failed", err);
        }
      }
      toast.success("Files uploaded");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemovePending = (id) => {
    setPendingFiles((prev) => {
      const removed = prev.find((p) => p.id === id);
      if (removed) revokePendingPreviews([removed]);
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleRemove = async (key) => {
    try {
      await removeInvoiceAttachment(invoiceId, key);
      setAttachments((prev) => prev.filter((k) => k !== key));
      toast.success("Attachment removed");
    } catch {
      // handled
    }
  };

  const propertyOpts = (properties || []).map((p) => ({
    value: p._id,
    label: p.title || p.name || p._id,
  }));

  const buildingOpts = (() => {
    const prop = (properties || []).find((p) => p._id === form.propertyId);
    return (prop?.building || [])
      .filter((b) => b?.buildingName)
      .map((b) => ({ value: b.buildingName, label: b.buildingName }));
  })();

  const categoryOpts = (categoryOptions || []).map((c) =>
    typeof c.value === "string" ? c : ({ value: c._id, label: c.name })
  );

  const statusOpts = INVOICE_STATUSES.map((s) => ({ value: s.value, label: s.value }));

  const lockedProperty = !!prefilledPropertyId && isNew;
  const lockedBuilding = !!prefilledBuildingName && isNew;

  const handleClose = (refresh) => {
    revokePendingPreviews(pendingFiles);
    setPendingFiles([]);
    onClose(refresh);
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={() => handleClose(false)} size="lg" centered backdrop="static">
        <ModalHeader toggle={() => handleClose(false)} style={{ borderBottom: "1px solid #EEF2F7" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <FeatherIcon icon={isNew ? "plus-circle" : "edit-2"} size={18} color="#4F46E5" />
            <div>
              <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0F172A" }}>
                {isNew ? "New Invoice" : `Edit Invoice ${invoiceData?.invoiceNo ? `#${invoiceData.invoiceNo}` : ""}`}
              </div>
              <div style={{ fontSize: "0.75rem", color: "#64748B", fontWeight: 500 }}>
                {isNew ? "Fill in details and attach files in one go" : "Update invoice details"}
              </div>
            </div>
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: "20px 24px" }}>
          <div className="row g-3">
            <div className="col-md-8">
              <Label style={LABEL_STYLE}>Invoice Title <span style={{ color: "#DC2626" }}>*</span></Label>
              <Input
                value={form.title}
                onChange={(e) => set("title")(e.target.value)}
                placeholder="e.g. February cleaning service"
                style={INPUT_STYLE}
              />
              {errors.title && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.title}</div>}
            </div>
            <div className="col-md-4">
              <Label style={LABEL_STYLE}>Reference No.</Label>
              <Input
                value={form.referenceNo}
                onChange={(e) => set("referenceNo")(e.target.value)}
                placeholder="Vendor invoice #"
                style={INPUT_STYLE}
              />
            </div>

            <div className="col-md-6">
              <Label style={LABEL_STYLE}>Vendor / Supplier <span style={{ color: "#DC2626" }}>*</span></Label>
              <Input
                value={form.vendorName}
                onChange={(e) => set("vendorName")(e.target.value)}
                placeholder="Vendor name"
                style={INPUT_STYLE}
              />
              {errors.vendorName && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.vendorName}</div>}
            </div>
            <div className="col-md-3">
              <Label style={LABEL_STYLE}>Vendor Email</Label>
              <Input
                value={form.vendorEmail}
                onChange={(e) => set("vendorEmail")(e.target.value)}
                placeholder="optional"
                style={INPUT_STYLE}
              />
            </div>
            <div className="col-md-3">
              <Label style={LABEL_STYLE}>Vendor Phone</Label>
              <Input
                value={form.vendorPhone}
                onChange={(e) => set("vendorPhone")(e.target.value)}
                placeholder="optional"
                style={INPUT_STYLE}
              />
            </div>

            <div className="col-md-4">
              <Label style={LABEL_STYLE}>Amount <span style={{ color: "#DC2626" }}>*</span></Label>
              <Input
                type="number" step="0.01" min="0"
                value={form.amount}
                onChange={(e) => set("amount")(e.target.value)}
                placeholder="0.00"
                style={INPUT_STYLE}
              />
              {errors.amount && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.amount}</div>}
              {form.amount !== "" && !Number.isNaN(Number(form.amount)) && (
                <div style={{ marginTop: 4, fontSize: "0.7rem", color: "#64748B" }}>
                  {formatCurrency(Number(form.amount), form.currency)}
                </div>
              )}
            </div>
            <div className="col-md-2">
              <Label style={LABEL_STYLE}>Currency</Label>
              <Select
                options={CURRENCY_OPTIONS}
                value={CURRENCY_OPTIONS.find((c) => c.value === form.currency)}
                onChange={(opt) => set("currency")(opt?.value || "CAD")}
                styles={selectStyles}
                menuPortalTarget={document.body} menuPosition="fixed"
              />
            </div>
            <div className="col-md-3">
              <Label style={LABEL_STYLE}>Invoice Date <span style={{ color: "#DC2626" }}>*</span></Label>
              <Input
                type="date"
                value={form.invoiceDate}
                onChange={(e) => set("invoiceDate")(e.target.value)}
                style={INPUT_STYLE}
              />
              {errors.invoiceDate && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.invoiceDate}</div>}
            </div>
            <div className="col-md-3">
              <Label style={LABEL_STYLE}>Due Date</Label>
              <Input
                type="date"
                value={form.dueDate}
                onChange={(e) => set("dueDate")(e.target.value)}
                style={INPUT_STYLE}
              />
            </div>

            <div className="col-md-6">
              <Label style={LABEL_STYLE}>Property <span style={{ color: "#DC2626" }}>*</span></Label>
              {lockedProperty ? (
                <Input value={prefilledPropertyLabel} disabled style={INPUT_STYLE} />
              ) : (
                <Select
                  options={propertyOpts}
                  value={propertyOpts.find((o) => o.value === form.propertyId) || null}
                  onChange={(opt) => {
                    set("propertyId")(opt?.value || "");
                    set("buildingName")("");
                  }}
                  styles={selectStyles}
                  placeholder="Choose property"
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              )}
              {errors.propertyId && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.propertyId}</div>}
            </div>
            <div className="col-md-6">
              <Label style={LABEL_STYLE}>Building <span style={{ color: "#DC2626" }}>*</span></Label>
              {lockedBuilding ? (
                <Input value={prefilledBuildingName} disabled style={INPUT_STYLE} />
              ) : (
                <Select
                  options={buildingOpts}
                  value={buildingOpts.find((o) => o.value === form.buildingName) || null}
                  onChange={(opt) => set("buildingName")(opt?.value || "")}
                  styles={selectStyles}
                  placeholder={form.propertyId ? "Choose building" : "Pick property first"}
                  isDisabled={!form.propertyId}
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              )}
              {errors.buildingName && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.buildingName}</div>}
            </div>

            <div className="col-md-6">
              <Label style={LABEL_STYLE}>Category <span style={{ color: "#DC2626" }}>*</span></Label>
              <Select
                options={categoryOpts}
                value={categoryOpts.find((o) => o.value === form.categoryId) || null}
                onChange={(opt) => set("categoryId")(opt?.value || "")}
                styles={selectStyles}
                placeholder="Choose category"
                menuPortalTarget={document.body} menuPosition="fixed"
              />
              {errors.categoryId && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.categoryId}</div>}
            </div>
            <div className="col-md-6">
              <Label style={LABEL_STYLE}>Status</Label>
              <Select
                options={statusOpts}
                value={statusOpts.find((o) => o.value === form.status)}
                onChange={(opt) => set("status")(opt?.value || "Unpaid")}
                styles={selectStyles}
                menuPortalTarget={document.body} menuPosition="fixed"
              />
            </div>

            <div className="col-12">
              <Label style={LABEL_STYLE}>Description / Notes</Label>
              <Input
                type="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => set("description")(e.target.value)}
                placeholder="Anything the team should know about this invoice…"
                style={{ ...INPUT_STYLE, height: "auto", padding: "10px 12px" }}
              />
            </div>

            {/* Attachments — staged before create, uploaded after */}
            <div className="col-12">
              <div style={{
                background: "#F8FAFC", border: "1px dashed #CBD5E1", borderRadius: 10,
                padding: 16, marginTop: 6,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.85rem" }}>
                      <FeatherIcon icon="paperclip" size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
                      Invoice Files & Attachments
                    </div>
                    <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 2 }}>
                      {invoiceId
                        ? "Upload the original invoice PDF, photos, receipts, or supporting docs."
                        : "Pick files now — they'll upload automatically when you create the invoice."}
                    </div>
                  </div>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      style={{ display: "none" }}
                      onChange={(e) => handleFiles(e.target.files)}
                    />
                    <button
                      type="button"
                      className="iv-btn iv-btn--primary"
                      disabled={uploading || saving}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploading ? <Spinner size="sm" /> : <FeatherIcon icon={invoiceId ? "upload" : "plus"} size={14} />}
                      {invoiceId ? "Upload" : "Add Files"}
                    </button>
                  </div>
                </div>
                {attachments.length === 0 && pendingFiles.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "16px 0", color: "#94A3B8", fontSize: "0.8rem" }}>
                    No attachments yet
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
                    {/* Pending (not yet on server) */}
                    {pendingFiles.map((pf) => {
                      const filename = pf.file.name;
                      const isImage = pf.file.type?.startsWith("image/");
                      const isPdf = /\.pdf$/i.test(filename) || pf.file.type === "application/pdf";
                      const isOffice = /\.(docx?|xlsx?|pptx?)$/i.test(filename);
                      return (
                        <div key={pf.id} className="iv-attach-card iv-attach-card--pending">
                          <div
                            className="iv-attach-card__preview"
                            onClick={() => isImage && pf.previewUrl && setPreviewPendingId(pf.id)}
                            style={{ cursor: isImage && pf.previewUrl ? "zoom-in" : "default" }}
                          >
                            {isImage && pf.previewUrl
                              ? <img src={pf.previewUrl} alt={filename} />
                              : isPdf
                                ? <FeatherIcon icon="file-text" size={32} color="#DC2626" />
                                : isOffice
                                  ? <FeatherIcon icon="file" size={32} color="#2563EB" />
                                  : <FeatherIcon icon="file" size={32} color="#64748B" />}
                            <span className="iv-attach-card__badge">PENDING</span>
                          </div>
                          <div className="iv-attach-card__name" title={filename}>{filename}</div>
                          <div className="iv-attach-card__actions">
                            <button
                              type="button"
                              onClick={() => handleRemovePending(pf.id)}
                              title="Remove"
                              style={{ color: "#DC2626" }}
                              disabled={saving}
                            >
                              <FeatherIcon icon="trash-2" size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {/* Server-confirmed attachments */}
                    {attachments.map((key) => {
                      const filename = key.split("/").pop();
                      const url = signedUrls[key];
                      const t = getFileTypeFromKey(key);
                      return (
                        <div key={key} className="iv-attach-card">
                          <div className="iv-attach-card__preview" onClick={() => url && setPreviewKey(key)}>
                            {t === "image" && url
                              ? <img src={url} alt={filename} />
                              : t === "pdf"
                                ? <FeatherIcon icon="file-text" size={32} color="#DC2626" />
                                : t === "office"
                                  ? <FeatherIcon icon="file" size={32} color="#2563EB" />
                                  : <FeatherIcon icon="file" size={32} color="#64748B" />}
                          </div>
                          <div className="iv-attach-card__name" title={filename}>{filename}</div>
                          <div className="iv-attach-card__actions">
                            <button
                              type="button"
                              onClick={() => url && setPreviewKey(key)}
                              title="Preview"
                            >
                              <FeatherIcon icon="eye" size={12} />
                            </button>
                            {url && (
                              <a href={url} target="_blank" rel="noopener noreferrer" title="Download">
                                <FeatherIcon icon="download" size={12} />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemove(key)}
                              title="Remove"
                              style={{ color: "#DC2626" }}
                            >
                              <FeatherIcon icon="trash-2" size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter style={{ borderTop: "1px solid #EEF2F7" }}>
          <Button
            color="secondary"
            outline
            onClick={() => handleClose(true)}
            disabled={saving || uploading}
          >
            {isNew && !createdId ? "Cancel" : "Done"}
          </Button>
          <button
            type="button"
            className="iv-btn iv-btn--primary"
            onClick={handleSave}
            disabled={saving || uploading}
          >
            {(saving || uploading) ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={14} />}
            {isNew
              ? createdId
                ? "Save Changes"
                : pendingFiles.length > 0
                  ? `Create Invoice & Upload ${pendingFiles.length}`
                  : "Create Invoice"
              : "Save"}
          </button>
        </ModalFooter>
      </Modal>

      <FilePreviewModal
        isOpen={!!previewKey}
        toggle={() => setPreviewKey(null)}
        signedUrl={previewKey ? signedUrls[previewKey] : null}
        fileKey={previewKey}
      />

      <FilePreviewModal
        isOpen={!!previewPendingId}
        toggle={() => setPreviewPendingId(null)}
        signedUrl={previewPendingId
          ? pendingFiles.find((p) => p.id === previewPendingId)?.previewUrl || null
          : null}
        fileKey={previewPendingId
          ? pendingFiles.find((p) => p.id === previewPendingId)?.file?.name || null
          : null}
      />
    </>
  );
};

export { InvoiceFormModal };
